import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import { Moment } from "moment";
import { tap, takeUntil } from "rxjs";
import { omit } from "lodash";
import { TranslateService } from "@root/shared/service/translate.service";

/** 0 - 独立房间  1 - 房型 */
type ParentEntityType = 0 | 1;

/**
 * bar - 房价日历专用 房型 和  房间
 */
type RenderType = "bar" | "group" | "member";

export interface RenderEntity {
  r_type: RenderType;
  r_index?: number;
}

export interface Bar extends RenderEntity {
  b_title: string;
}

export interface Parent extends RenderEntity {
  p_id: number;
  p_title: string;
  p_type: ParentEntityType;
  p_children_ids: number[];
  p_children: Child[];

  p_stop_auto_managed: boolean;
}

export interface Child extends RenderEntity {
  c_id: number;
  c_title: string;
  c_cover_url: string;
  c_parent: Parent;
  /** 以下字段为暂时兼容 */
  c_cleaning_status: number;
  c_stop_auto_managed: boolean;
}

export type PriceChild = Child & {
  thirdparty_type: number;
  thirdparty_house_title: string;
  c_readonly: boolean;
  // 只有 readonly 字段为 true 时才有该字段
  c_rate_plan_desc: string;
  [props: string]: any;
};

export interface DateRange {
  head: Moment;
  last: Moment;
}

export type entityType = "house" | "type";
export type operateType = "status" | "price";
type entityPayload<T extends entityType, U extends operateType> = T extends "house"
  ? U extends "status"
    ? Child
    : PriceChild
  : Parent;
export interface EntityData<T extends entityType, U extends operateType> {
  /** @deprecated */
  id?: number | string;
  type?: T;
  payload?: entityPayload<T, U>;
}

export interface SelectedData<T extends entityType, U extends operateType> {
  status: "none" | "doing" | "done";
  dates: DateRange;
  data?: EntityData<T, U>;
  token?: number; // 单日历用于标识发消息的个体
}

export interface HourReservationSidebarData {
  house: {
    id: any;
    house_title: any;
    [props: string]: any;
  };
  date: string | Moment;
}

export type TabName =
  | "manual-order"
  | "hostex-status"
  | "hostex-price"
  | "channel-price"
  | "rules-setting"
  | "channel-status"
  | "hour-room-order";

export interface ChangeOrderBelongedHouseData {
  action: "Select" | "Confirm" | "Cancel";
  payload: {
    code?: string;
    index?: number;
    width?: number;
    houseId?: number;
  };
}

@Injectable({
  providedIn: "root",
})
export class MultiHouseCalendarService {
  selectedData: SelectedData<entityType, operateType> = null;

  hourReservationData = null;

  selectRaw$ = new Subject<SelectedData<entityType, operateType>>();
  singlePropertySelectRaw$ = new Subject<SelectedData<entityType, operateType>>();
  selectReservationRaw$ = new Subject<string>();
  singlePropertySelectResvRaw$ = new Subject<string>();
  selectTaskRaw$ = new Subject<any>();
  dateChangeRaw$ = new Subject<[string, string]>();
  scrollRaw$ = new Subject<number | string | Moment>();
  calendarRefreshRaw$ = new Subject();
  taskRaw$ = new Subject<{ type: "remove"; id: number }>();

  reservationRaw$ = new Subject<{ type: "remove"; code: string }>();
  sidebarTabRaw$ = new Subject<{ type: TabName; data?: any }>();

  requestUpdateAvailableRaw$ = new Subject<0 | 1>();
  requestUpdateChannelAvailableRaw$ = new Subject<{
    available: 0 | 1;
    thirdparty?: number;
  }>();

  orderBoxCountRaw$ = new Subject<number>();

  hourReservationRaw$ = new Subject<HourReservationSidebarData>();
  changeOrderBelongedHouseRaw$ = new Subject<ChangeOrderBelongedHouseData>();
  private select$ = this.selectRaw$.pipe(tap((value) => (this.selectedData = value)));

  private hourReservationSidebar$ = this.hourReservationRaw$.pipe(tap((value) => (this.hourReservationData = value)));
  private destroy$ = new Subject();

  constructor(private t: TranslateService) {
    this.select$.pipe(takeUntil(this.destroy$)).subscribe();
    this.hourReservationSidebar$.pipe(takeUntil(this.destroy$)).subscribe();
  }

  scroll(value: number | string | Moment) {
    this.scrollRaw$.next(value);
  }

  select(value: SelectedData<entityType, operateType>) {
    this.selectRaw$.next(value);
  }

  singlePropertySelect(value: SelectedData<entityType, operateType>) {
    this.singlePropertySelectRaw$.next(value);
  }

  selectReservation(code: string) {
    this.selectReservationRaw$.next(code);
  }

  singlePropertySelectResv(code: string) {
    // this.
  }

  selectTask(data: { taskId: string; date: string; houseId: number }) {
    this.selectTaskRaw$.next(data);
  }

  refresh() {
    this.calendarRefreshRaw$.next(null);
  }

  deleteTask(id: number) {
    this.taskRaw$.next({ type: "remove", id });
  }

  removeReservation(code: string) {
    this.reservationRaw$.next({ type: "remove", code });
  }

  changeTab(name: TabName, data?) {
    this.sidebarTabRaw$.next({ type: name, data: data });
  }

  cancelSelect() {
    this.select({
      status: "none",
      dates: {
        head: null,
        last: null,
      },
    });
  }

  cancelSelectReservation() {
    this.selectReservation(null);
  }

  cancelSelectTask() {
    this.selectTask(null);
  }

  requestUpdateAvailable(value: 0 | 1) {
    this.requestUpdateAvailableRaw$.next(value);
  }

  requestUpdateChannelAvailable(available: 0 | 1, thirdparty?) {
    this.requestUpdateChannelAvailableRaw$.next({ available, thirdparty });
  }

  updateOrderBoxCount(number: number) {
    this.orderBoxCountRaw$.next(number);
  }

  displayHourReservationSidebar(data: HourReservationSidebarData) {
    this.hourReservationRaw$.next(data);
  }

  // 4.0.3 refactor
  generateStatusParentEntity(data: any, children: Child[] = []): Parent {
    return {
      p_id: data.house_type_id,
      p_title: data.house_type_title,
      p_type: data.house_type_id === -1 ? 0 : 1,
      p_children_ids: data.house_ids,
      p_stop_auto_managed: data.stop_auto_managed,
      p_children: children,
      ...data,
    };
  }

  generateStatusChildEntity(data: any, parent: Parent = null): Child {
    return {
      c_id: data.id,
      c_title: data.house_title,
      c_cover_url: data.cover_url,
      c_cleaning_status: data.cleaning_status,
      c_stop_auto_managed: data.stop_auto_managed,
      c_parent: parent,
      ...data,
      toJSON: function () {
        return omit(this, ["c_parent"]);
      },
    };
  }

  generatePriceBarEntity(data: { type: 1 | 0 }): Bar {
    return {
      b_title: this.t.t(
        data.type === 1
          ? "component.multi_house_price_calendar.multiple_inventory_house_type"
          : "component.multi_house_price_calendar.private_room",
      ),
      r_type: "bar",
    };
  }

  generatePriceParentEntity(data: any, children: Child[] = []): Parent {
    return {
      p_id: data.relation_id,
      p_title: data.title,
      p_type: data.type,
      p_children_ids: data.unique_keys,
      p_children: children,
      r_type: "group",
      ...data,

      toJSON: function () {
        return omit(this, ["p_children"]);
      },
    };
  }

  generatePriceChildEntiry(data: any, parent: Parent = null): PriceChild {
    return {
      c_id: data.unique_key,
      c_title: data.thirdparty_house_title,
      c_cover_url: data.cover_url,
      c_parent: parent,
      c_readonly: data.readonly,
      c_rate_plan_desc: data.desc,
      r_type: "member",
      ...data,

      toJSON: function () {
        return omit(this, ["c_parent"]);
      },
    };
  }
}
